import { useEffect, useState } from "react";
import { Loader2, RefreshCw, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fetchEmbeddingJob, fetchPublishAudit, drainEmbeddingQueue } from "@/lib/contentOps/api";
import { toast } from "sonner";

interface Props { articleId: string }

const PublishObservability = ({ articleId }: Props) => {
  const [job, setJob] = useState<Awaited<ReturnType<typeof fetchEmbeddingJob>>>(null);
  const [audit, setAudit] = useState<Awaited<ReturnType<typeof fetchPublishAudit>>>([]);
  const [loading, setLoading] = useState(false);
  const [draining, setDraining] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [j, a] = await Promise.all([fetchEmbeddingJob(articleId), fetchPublishAudit(articleId)]);
      setJob(j); setAudit(a);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [articleId]);

  const drain = async () => {
    setDraining(true);
    try {
      const r = await drainEmbeddingQueue();
      toast.success("Queue processed", { description: `${r.done} embedded · ${r.skipped} skipped · ${r.errors} errors` });
      await load();
    } catch (e) {
      toast.error("Drain failed", { description: (e as Error).message });
    } finally { setDraining(false); }
  };

  const jobBadge = (() => {
    if (!job) return { label: "No queue activity", color: "hsl(0 0% 55%)", bg: "hsl(0 0% 12%)" };
    if (job.status === "done")    return { label: "Embedded", color: "hsl(142 70% 60%)", bg: "hsl(142 70% 35% / 0.15)" };
    if (job.status === "pending") return { label: "Queued",   color: "hsl(38 92% 65%)",  bg: "hsl(38 92% 45% / 0.15)" };
    if (job.status === "running") return { label: "Running",  color: "hsl(207 68% 65%)", bg: "hsl(207 68% 45% / 0.15)" };
    return { label: "Error", color: "hsl(0 75% 65%)", bg: "hsl(0 75% 45% / 0.15)" };
  })();

  return (
    <div className="rounded-xl border p-5" style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold" style={{ color: "hsl(0 0% 90%)" }}>Lifecycle & audit</h3>
        <Button size="sm" variant="ghost" onClick={load} disabled={loading}
          className="h-7 px-2 text-xs" style={{ color: "hsl(0 0% 70%)" }}>
          {loading ? <Loader2 size={11} className="animate-spin mr-1" /> : <RefreshCw size={11} className="mr-1" />}
          Refresh
        </Button>
      </div>

      <div className="rounded-lg border p-3 mb-3" style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 16%)" }}>
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wider mb-1" style={{ color: "hsl(0 0% 50%)" }}>Embedding</div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold"
                style={{ background: jobBadge.bg, color: jobBadge.color }}>{jobBadge.label}</span>
              {job && <span className="text-[11px]" style={{ color: "hsl(0 0% 55%)" }}>
                reason: <code>{job.reason}</code> · attempts {job.attempts}
              </span>}
            </div>
            {job?.last_error && (
              <div className="flex items-start gap-1 mt-1.5 text-[11px]" style={{ color: "hsl(0 75% 65%)" }}>
                <AlertTriangle size={11} className="mt-0.5" /> {job.last_error}
              </div>
            )}
          </div>
          <Button size="sm" variant="outline" onClick={drain} disabled={draining}
            className="border h-7 text-xs whitespace-nowrap"
            style={{ borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 85%)", background: "transparent" }}>
            {draining ? <Loader2 size={11} className="animate-spin mr-1" /> : null}
            Run worker
          </Button>
        </div>
      </div>

      <div className="text-[11px] uppercase tracking-wider mb-2" style={{ color: "hsl(0 0% 50%)" }}>Recent publish events</div>
      {audit.length === 0 ? (
        <p className="text-xs" style={{ color: "hsl(0 0% 50%)" }}>No publish events yet.</p>
      ) : (
        <ul className="space-y-1">
          {audit.slice(0, 8).map((e) => (
            <li key={e.id} className="flex items-center justify-between text-[11px] rounded border px-2 py-1.5"
              style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 14%)", color: "hsl(0 0% 80%)" }}>
              <span className="flex items-center gap-2">
                <CheckCircle2 size={11} style={{ color: "hsl(142 70% 60%)" }} />
                <strong style={{ color: "hsl(0 0% 92%)" }}>{e.action}</strong>
                {e.reason && <span style={{ color: "hsl(0 0% 55%)" }}>· {e.reason}</span>}
              </span>
              <time style={{ color: "hsl(0 0% 50%)" }}>{new Date(e.created_at).toLocaleString()}</time>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PublishObservability;
